import React, { useState } from 'react';
import { observer } from 'mobx-react';
import Routes from 'routes';
import { customFetch } from 'utils/fetch';

import ConfirmationOverlay from 'shared/ConfirmationOverlay';
import type FractionsStore from 'productionOrders/stores/FractionsStore';
import { addTestingIdentifier } from 'utils/testing';

export interface ConfirmFinalisedFractionProps {
  id: number;
  // PLA-860: en el flujo nuevo se vuelve a las fracciones para aprobar la muestra general.
  redirectPath?: string;
  // PLA-860 v2 (D6): corrida vigente, para avisar que subdivisiones se aprobaran al confirmar.
  fractionsStore?: FractionsStore;
}

const ConfirmFinalisedFractionComponent: React.FC<ConfirmFinalisedFractionProps> = ({
  id,
  redirectPath,
  fractionsStore,
}) => {
  const [showOverlay, setOverlay] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const pendingSubdivisions = fractionsStore?.pendingSubdivisions || [];

  const handleFinalizeClick = () => {
    setErrors([]);
    setOverlay(true);
  };

  const handleOnPrimaryClick = async () => {
    if (submitted) {
      return;
    }

    setSubmitted(true);

    const response = await customFetch(
      Routes.fractionate_milling_order_path(id),
      {},
      'PATCH'
    );

    // Si el servidor no confirmo las fracciones no se redirige: se muestra el error.
    if (response.success) {
      window.location.href = redirectPath || Routes.milling_orders_path();
    } else {
      setErrors(response.errors?.length ? response.errors : [response.error]);
      setSubmitted(false);
    }
  };

  const handleOnSecondaryClick = () => setOverlay(false);

  return (
    <>
      {showOverlay && (
        <ConfirmationOverlay
          title={I18n.translate('fractions.confirm')}
          confirmText={I18n.translate('buttons.confirm')}
          onConfirmClick={handleOnPrimaryClick}
          cancelText={I18n.translate('buttons.cancel')}
          primaryDisabled={submitted}
          onCancelClick={handleOnSecondaryClick}
        >
          {pendingSubdivisions.length > 0 && (
            <p className='px-2' {...addTestingIdentifier('confirm_pending_subdivisions')}>
              {I18n.translate('fractions.confirm_pending_subdivisions', {
                count: pendingSubdivisions.length,
                identifiers: pendingSubdivisions
                  .map((fraction) => fraction.node_identifier)
                  .join(', '),
              })}
            </p>
          )}
          {errors.filter(Boolean).map((error) => (
            <p key={error} className='px-2 text-red'>
              {error}
            </p>
          ))}
        </ConfirmationOverlay>
      )}

      <button
        onClick={handleFinalizeClick}
        type='button'
        className='button'
        {...addTestingIdentifier('finalize_fraction_button')}
      >
        {I18n.translate('buttons.finalize')}
      </button>
    </>
  );
};

export default observer(ConfirmFinalisedFractionComponent);
